import { httpsify } from './url';

let cseq = 0;
export const makeClipId = () => `c${Date.now()}${cseq++}`;

// mm:ss
export function fmtTime(s) {
  if (s == null || Number.isNaN(s)) return '–';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// Same, but with two-decimal seconds (m:ss.ff) — for playhead readouts that
// need frame-level correlation (e.g. placing speed keyframes).
export function fmtTimePrecise(s) {
  if (s == null || Number.isNaN(s)) return '–';
  const m = Math.floor(s / 60);
  const sec = Math.max(0, s - m * 60);
  return `${m}:${sec.toFixed(2).padStart(5, '0')}`;
}

// Playback speed multiplier for a clip (2 = twice as fast, 0.5 = half/slow-mo).
// Clamped to a sane range; defaults to 1 (normal speed).
// FLAT multi-key speedKeys (all equal) still mean "play at the KEY speed" —
// mirrored in backend/api/render.js clipSpeed so the bake's constant-speed
// branch agrees with the keyed preview/timeline math (otherwise the encode
// drifts from the planned timeline and every later clip boundary shifts).
export function clipSpeed(clip) {
  const raw = Array.isArray(clip?.speedKeys) ? clip.speedKeys : [];
  if (raw.length >= 2) {
    const v = Number(raw[0].speed);
    if (Number.isFinite(v) && v > 0 && raw.every((k) => Math.abs((Number(k.speed) || 0) - v) < 1e-3)) {
      return Math.min(10, Math.max(0.1, v));
    }
  }
  const s = Number(clip?.speed);
  return Number.isFinite(s) && s > 0 ? Math.min(10, Math.max(0.1, s)) : 1;
}

// The playback-speed multiplier that makes an overcranked (slow-mo) clip play
// at real-life motion — i.e. its record rate ÷ project rate. Stored on the clip
// as `realSpeedX` when it's added (from the asset's RED/capture metadata).
// Returns 0 when the clip isn't slow-mo or we don't know.
export function clipRealSpeed(clip) {
  const x = Number(clip?.realSpeedX);
  return Number.isFinite(x) && x > 1.01 ? Math.round(x * 100) / 100 : 0;
}

// Derive the real-motion speed-up from a source asset's metadata. Mirrors the
// convention used in AssetDetails: prefer RED's trueSpeedX, else record/project
// fps, else capture vs container fps.
export function assetRealSpeedX(a) {
  if (!a) return 0;
  const red = a.redMeta;
  if (red) {
    if (Number(red.trueSpeedX) > 1.01) return Math.round(Number(red.trueSpeedX) * 100) / 100;
    if (red.recordFps && red.fps && red.recordFps > red.fps * 1.05) {
      return Math.round((red.recordFps / red.fps) * 100) / 100;
    }
  }
  const cap = a.captureFps || a.srcFps || a.metadata?.video?.captureFps || 0;
  const base = a.fpsAvg || a.metadata?.video?.fpsAvg || a.fps || 0;
  if (cap && base && cap > base * 1.05) return Math.round((cap / base) * 100) / 100;
  return 0;
}

// Source seconds a clip reads from its file (out − in), before speed.
export function clipSourceLength(clip) {
  const out = clip.outSec && clip.outSec > 0 ? clip.outSec : clip.sourceDurationSec || 0;
  const inn = clip.inSec || 0;
  return Math.max(0, out - inn);
}

// ---- variable speed (ramps) ----------------------------------------------
// A clip can ramp its speed over time via `speedKeys: [{ t, speed }]` where t is
// SOURCE seconds (relative to inSec, 0..srcLen). Speed interpolates linearly
// between keys (a ramp); equal neighbors hold; outside the range it holds the
// nearest key. Empty/<2 keys => the fixed `clip.speed`. The retime math is
// mirrored in backend/api/render.js so preview == bake.
const clampSpeedVal = (s) => (Number.isFinite(s) && s > 0 ? Math.min(10, Math.max(0.1, s)) : 1);

export function clipSpeedKeys(clip) {
  const raw = Array.isArray(clip?.speedKeys) ? clip.speedKeys : [];
  return raw
    .map((k) => ({ t: Math.max(0, Number(k.t) || 0), speed: clampSpeedVal(Number(k.speed)) }))
    .filter((k) => Number.isFinite(k.t) && Number.isFinite(k.speed))
    .sort((a, b) => a.t - b.t);
}

// True when the clip's speed actually varies over time (a ramp).
export function hasSpeedRamp(clip) {
  const ks = clipSpeedKeys(clip);
  return ks.length >= 2 && ks.some((k) => Math.abs(k.speed - ks[0].speed) > 1e-3);
}

// Effective playback speed at SOURCE second s (held flat outside the key range).
export function speedAtSource(clip, s) {
  const ks = clipSpeedKeys(clip);
  if (ks.length < 2) return clipSpeed(clip);
  if (s <= ks[0].t) return ks[0].speed;
  const last = ks[ks.length - 1];
  if (s >= last.t) return last.speed;
  for (let i = 1; i < ks.length; i += 1) {
    if (s <= ks[i].t) {
      const a = ks[i - 1];
      const b = ks[i];
      const f = (s - a.t) / ((b.t - a.t) || 1);
      return a.speed + (b.speed - a.speed) * f;
    }
  }
  return last.speed;
}

// Timeline seconds spanned by a constant-or-linear-ramp speed segment of source
// duration d going va -> vb. Exact integral of 1/v(s): d·ln(vb/va)/(vb-va).
function segTimelineDur(va, vb, d) {
  if (d <= 0) return 0;
  if (Math.abs(va - vb) < 1e-6) return d / va;
  return (d * Math.log(vb / va)) / (vb - va);
}

// Length of a clip on the MOVIE timeline. Speed compresses/expands it: a 10s
// source at 2× occupies 5s; at 0.5× (slow-mo) it occupies 20s. Ramp-aware.
export function clipTimelineLength(clip) {
  const srcLen = clipSourceLength(clip);
  const ks = clipSpeedKeys(clip);
  if (!srcLen || ks.length < 2) return srcLen / clipSpeed(clip);
  let tl = ks[0].t / ks[0].speed; // hold before the first key
  for (let i = 1; i < ks.length; i += 1) {
    const a = ks[i - 1];
    const b = ks[i];
    const end = Math.min(b.t, srcLen);
    if (end <= a.t) break;
    // If the trim cuts this ramp segment short, integrate only up to the speed
    // actually reached at the cut — using b.speed would overstate the retime
    // and disagree with playback (clipSpeedMap) and the bake (retimeChunks).
    const vEnd = end < b.t ? speedAtSource(clip, end) : b.speed;
    tl += segTimelineDur(a.speed, vEnd, end - a.t);
    if (b.t >= srcLen) return tl;
  }
  const last = ks[ks.length - 1];
  if (last.t < srcLen) tl += (srcLen - last.t) / last.speed;
  return tl;
}

export function clipLength(clip) {
  return clipTimelineLength(clip);
}

// Fast two-way SOURCE<->TIMELINE map for the live player (handles ramps). For a
// fixed-speed clip it's a simple ratio; for a ramp it samples the curve (few
// keys, so cheap) into cumulative arrays for binary-search interpolation.
export function clipSpeedMap(clip) {
  const srcLen = clipSourceLength(clip);
  const ks = clipSpeedKeys(clip);
  if (!srcLen || ks.length < 2) {
    const v = clipSpeed(clip);
    return { srcLen, tlLen: srcLen / v, ramp: false, v, maxSpeed: v };
  }
  // Sample boundaries: 0, each key.t, srcLen — subdivide ramp intervals.
  const bounds = [0];
  for (const k of ks) if (k.t > 0 && k.t < srcLen) bounds.push(k.t);
  bounds.push(srcLen);
  const src = [0];
  const tl = [0];
  let acc = 0;
  let maxSpeed = 0;
  for (let i = 1; i < bounds.length; i += 1) {
    const s0 = bounds[i - 1];
    const s1 = bounds[i];
    const sub = Math.max(1, Math.ceil((s1 - s0) / 0.05));
    const ds = (s1 - s0) / sub;
    for (let j = 1; j <= sub; j += 1) {
      const a = s0 + (j - 1) * ds;
      const b = s0 + j * ds;
      const va = speedAtSource(clip, a);
      const vb = speedAtSource(clip, b);
      maxSpeed = Math.max(maxSpeed, va, vb);
      acc += segTimelineDur(va, vb, ds);
      src.push(b);
      tl.push(acc);
    }
  }
  return { srcLen, tlLen: acc, ramp: true, src, tl, maxSpeed: maxSpeed || clipSpeed(clip) };
}

function interpArr(xs, ys, x) {
  const n = xs.length;
  if (n === 0) return 0;
  if (x <= xs[0]) return ys[0];
  if (x >= xs[n - 1]) return ys[n - 1];
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] <= x) lo = mid; else hi = mid;
  }
  const f = (x - xs[lo]) / ((xs[hi] - xs[lo]) || 1);
  return ys[lo] + (ys[hi] - ys[lo]) * f;
}

// SOURCE second -> TIMELINE second within the clip (using a clipSpeedMap).
export function srcToTimeline(map, s) {
  if (!map || !map.ramp) return Math.max(0, s) / (map ? map.v : 1);
  return interpArr(map.src, map.tl, Math.max(0, s));
}
// TIMELINE second -> SOURCE second within the clip (using a clipSpeedMap).
export function timelineToSrc(map, tlSec) {
  if (!map || !map.ramp) return Math.max(0, tlSec) * (map ? map.v : 1);
  return interpArr(map.tl, map.src, Math.max(0, tlSec));
}

// Fallback frame rate when a clip's real fps hasn't been measured yet.
export const DEFAULT_FPS = 30;

// Snap a noisy measured fps to the nearest standard rate so the readout is clean.
export function snapFps(raw) {
  if (!raw || !Number.isFinite(raw) || raw <= 0) return 0;
  const common = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 90, 100, 119.88, 120, 144, 240];
  let best = common[0];
  let bestErr = Infinity;
  for (const c of common) {
    const err = Math.abs(c - raw);
    if (err < bestErr) { bestErr = err; best = c; }
  }
  // Only snap when we're within ~4% — otherwise trust the raw measurement.
  return bestErr / best <= 0.04 ? best : Math.round(raw * 100) / 100;
}

// Which clip a movie-time falls in, plus the 1-based frame number relative to
// that clip's TRIMMED start (so the first kept frame is always frame 1).
// `clips` are the raw clip list (each with optional inSec/outSec/fps).
export function frameAtMovieTime(clips, t) {
  if (!Array.isArray(clips) || !clips.length || t == null || t < 0) return null;
  let acc = 0;
  for (let i = 0; i < clips.length; i += 1) {
    const c = clips[i];
    const len = clipLength(c);
    if (t < acc + len || i === clips.length - 1) {
      const localMovie = Math.max(0, t - acc); // seconds into this clip on the movie timeline
      const speed = clipSpeed(c);
      // Ramp-aware timeline→source mapping (a constant `localMovie * speed`
      // overshoots badly once the clip has speed keyframes).
      const localSource = Math.min(clipSourceLength(c), timelineToSrc(clipSpeedMap(c), localMovie));
      const fps = c.fps || DEFAULT_FPS;
      const srcLen = clipSourceLength(c);
      return {
        idx: i,
        clip: c,
        label: c.label || `Clip ${i + 1}`,
        localSec: localMovie,
        localSourceSec: localSource,
        sourceSec: (c.inSec || 0) + localSource,
        speed,
        fps,
        approxFps: !c.fps,
        // Frame is counted in the SOURCE footage (the real recorded frames),
        // relative to the trim in-point so the first kept frame is frame 1.
        frame: Math.floor(localSource * fps) + 1,
        totalFrames: Math.max(1, Math.round(srcLen * fps)),
      };
    }
    acc += len;
  }
  return null;
}

// Normalize a movie into an ordered clip list with absolute movie-time starts.
// Back-compat: a movie with only `videoUrl`/`streamUrl` becomes one clip.
export function movieClips(movie) {
  if (!movie) return [];
  let clips = Array.isArray(movie.clips) ? movie.clips : [];
  if (clips.length === 0) {
    const url = movie.videoUrl || movie.streamUrl;
    if (!url) return [];
    clips = [
      {
        id: 'legacy',
        label: movie.title || 'Clip',
        url,
        inSec: 0,
        outSec: movie.durationSec || 0,
        sourceDurationSec: movie.durationSec || 0,
      },
    ];
  }
  let t = 0;
  return clips.map((c) => {
    const len = clipLength(c);
    const seg = { ...c, len, startInMovie: t };
    t += len;
    return seg;
  });
}

export function movieDuration(movie) {
  return movieClips(movie).reduce((sum, c) => sum + c.len, 0);
}

// Per-clip placement on the COMPOSED MASTER timeline (what the reframe master MP4
// and social export actually are). Unlike movieClips()/movieDuration() — which
// assume hard cuts — this mirrors backend/api/render.js: a cross-dissolve makes
// the next clip start `dissolveSec` earlier, so the master is shorter than the
// sum of clip lengths. Returns one entry per clip with:
//   start/end  : the clip's own span in master seconds (overlaps neighbors by the
//                dissolve during a transition)
//   tileStart/tileEnd : NON-overlapping tiles that tile [0,total] exactly, so a
//                given master time maps to exactly one clip (used for per-clip
//                crop preview + export segmentation).
export function masterSegments(clips) {
  const cs = Array.isArray(clips) ? clips : [];
  const n = cs.length;
  if (!n) return [];
  const segs = [];
  let curDur = clipLength(cs[0]);
  segs.push({ idx: 0, clip: cs[0], start: 0, end: curDur });
  for (let i = 1; i < n; i += 1) {
    const li = clipLength(cs[i]);
    const d = Math.max(0, Math.min(cs[i - 1].dissolveSec || 0, Math.min(curDur, li, 3)));
    const start = Math.max(0, curDur - d);
    curDur = curDur + li - d;
    segs.push({ idx: i, clip: cs[i], start, end: curDur });
  }
  const total = curDur;
  return segs.map((s, i) => ({
    ...s,
    tileStart: s.start,
    tileEnd: i < n - 1 ? segs[i + 1].start : total,
  }));
}

// Total length of the composed master (dissolve-aware), matching render.js.
export function masterDuration(clips) {
  const segs = masterSegments(clips);
  return segs.length ? segs[segs.length - 1].end : 0;
}

// Index of the clip whose non-overlapping tile contains master time t.
export function clipIndexAtMaster(segs, t) {
  if (!Array.isArray(segs) || !segs.length) return -1;
  for (let i = 0; i < segs.length; i += 1) {
    if (t < segs[i].tileEnd || i === segs.length - 1) return i;
  }
  return segs.length - 1;
}

// ---- EDIT <-> MASTER time-base conversion -----------------------------------
// The same cut lives on TWO clocks: the EDIT timeline (hard cuts, Σ clipLength —
// what the shared WaveTimeline, Assemble player and Color tab use) and the
// MASTER timeline (the dissolve-composed preview master the Reframe tab plays,
// where each transition overlaps its clips by dissolveSec). Every dissolve makes
// the master SHORTER than the edit, so a raw handoff of seconds between the two
// drifts further out of sync per transition. Convert at every boundary instead.
export function editToMaster(clips, t) {
  const segs = masterSegments(clips);
  if (!segs.length) return Math.max(0, t || 0);
  let acc = 0;
  for (let i = 0; i < segs.length; i += 1) {
    const len = clipLength(segs[i].clip);
    if ((t || 0) < acc + len || i === segs.length - 1) {
      const local = Math.max(0, Math.min((t || 0) - acc, len));
      return Math.min(segs[i].start + local, segs[segs.length - 1].end);
    }
    acc += len;
  }
  return segs[segs.length - 1].end;
}

export function masterToEdit(clips, t) {
  const segs = masterSegments(clips);
  if (!segs.length) return Math.max(0, t || 0);
  const i = clipIndexAtMaster(segs, Math.max(0, t || 0));
  const seg = segs[i];
  let acc = 0;
  for (let j = 0; j < i; j += 1) acc += clipLength(segs[j].clip);
  const local = Math.max(0, Math.min((t || 0) - seg.start, clipLength(seg.clip)));
  return acc + local;
}

// Fingerprint of just the COMPOSITION baked into the flat preview master (clip
// order, source, trim, retime, dissolves — grades and songs don't affect it).
// Stamped onto master.cutSig when a build is requested; later, a mismatch with
// the live clips flags the master stale even when the total duration happens to
// still agree (e.g. a reorder, or an in/out slip that keeps the length).
export function masterCutSig(clips) {
  return JSON.stringify((clips || []).map((c) => [
    c.url || c.s3Key || '',
    c.inSec || 0,
    c.outSec || 0,
    c.dissolveSec || 0,
    c.speed || 1,
    Array.isArray(c.speedKeys) && c.speedKeys.length
      ? c.speedKeys.map((k) => `${k.t}@${k.speed}`).join(',')
      : '',
  ]));
}

// Default (no-op) per-clip reframe config. `manualCrop.scale` is the crop size
// (1 = widest the ratio allows) shared by both modes; subject mode also pans.
export const DEFAULT_CLIP_REFRAME = {
  cropMode: 'subject',
  manualCrop: { cx: 0.5, cy: 0.5, scale: 1 },
  subjectTrackId: null,
  // Other detected subjects the editor flagged for THIS clip (kept for quick
  // re-tagging / reference). Only `subjectTrackId` (the primary) drives the crop.
  secondaryTrackIds: [],
  waypoints: [],
  // Optional zoom keyframes [{ t, scale }] in MASTER time. When present the crop
  // size animates over the clip (e.g. start wide, push in later); when empty the
  // single manualCrop.scale is used for the whole clip.
  zoomKeys: [],
  // Path smoothing — the EMA alpha applied to the subject pan (and zoom) so the
  // crop glides between keyframes instead of snapping. Lower = smoother/laggier,
  // 1 = follow keyframes exactly. Mirrored by the backend export for parity.
  smoothing: 0.35,
};

// Normalize a clip's stored reframe into a complete config.
export function clipReframe(clip) {
  const r = (clip && clip.reframe) || {};
  const mc = r.manualCrop || {};
  return {
    cropMode: r.cropMode === 'manual' ? 'manual' : 'subject',
    manualCrop: {
      cx: mc.cx == null ? 0.5 : mc.cx,
      cy: mc.cy == null ? 0.5 : mc.cy,
      scale: mc.scale == null ? 1 : mc.scale,
    },
    subjectTrackId: r.subjectTrackId || null,
    secondaryTrackIds: Array.isArray(r.secondaryTrackIds) ? r.secondaryTrackIds : [],
    waypoints: Array.isArray(r.waypoints) ? r.waypoints : [],
    zoomKeys: Array.isArray(r.zoomKeys) ? r.zoomKeys : [],
    smoothing: Number.isFinite(Number(r.smoothing))
      ? Math.min(1, Math.max(0.05, Number(r.smoothing)))
      : 0.35,
  };
}

// A resolved, https playback URL for a clip (CDN url, else raw s3Key presign).
// resolver is uploadService.getAssetPlaybackUrl.
export async function resolveClipUrl(clip, resolver) {
  if (clip.url) return httpsify(clip.url);
  if (clip.s3Key && resolver) return resolver(clip.s3Key);
  return '';
}

// ---- SDK preview proxies ---------------------------------------------------
// movie.sdkPreviews[clipId] is a clip-window edit proxy decoded through the
// clip's chosen RED SDK development (frameProofs worker, kind 'sdk-preview'),
// so the editor can preview the TRUE de-log instead of flat log + a WebGL
// approximation. The preview's t=0 corresponds to source second `startSec`.

// The SDK development a RED clip will actually bake with: clip.redDecode wins
// over the movie-wide output.redPipeline/redExposure. Mirrors redDecodeFor in
// backend/api/render.js — 'default' means a flat-log intermediate (the app
// grades it later), for which an SDK preview would LIE about the bake.
export function effectiveRedDecode(clip, output) {
  const d = (clip && clip.redDecode) || {};
  const o = output || {};
  return {
    pipeline: d.pipeline || o.redPipeline || 'default',
    exposure: d.exposure != null && Number.isFinite(Number(d.exposure)) ? Number(d.exposure) : (Number(o.redExposure) || 0),
    toneMap: d.toneMap || '',
    rolloff: d.rolloff || '',
  };
}

// The clip's SDK preview, or null when it can't be trusted: it must still cover
// the clip's trimmed window (the worker pads the decode — trim headroom) and
// match the development the clip currently bakes with. Clips whose bake stays
// flat log, or whose saved grade was authored for the flat-log proxy (a log
// input transform would double de-log over an SDK-developed base), keep the
// ordinary proxy path.
export function sdkPreviewFor(sdkPreviews, clip, output) {
  const sp = sdkPreviews && clip ? sdkPreviews[clip.id] : null;
  if (!sp || !sp.url || !clip.isRawVideo) return null;
  const it = clip.grade && clip.grade.inputTransform;
  if (clip.useGrade !== false && it && it !== 'srgb') return null;
  const want = effectiveRedDecode(clip, output);
  if (want.pipeline === 'default') return null;
  const got = sp.redDecode || {};
  if ((got.pipeline || '') !== want.pipeline) return null;
  if (Math.abs((Number(got.exposure) || 0) - want.exposure) > 0.001) return null;
  if ((got.toneMap || '') !== want.toneMap || (got.rolloff || '') !== want.rolloff) return null;
  if ((clip.inSec || 0) < (sp.startSec || 0) - 0.02) return null;
  if ((clip.outSec || 0) > (sp.endSec || 0) + 0.02) return null;
  return sp;
}

// ---- Audio / video effect math (shared by preview + render) ----------------
// A song's play length in movie-time (seconds it occupies the timeline).
// A looping song fills the movie from its offset to the end, so its length
// depends on the movie duration — pass movieDur to get the loop-extended
// length (omit it for the un-looped slice length, e.g. block width math).
export function songLength(s, movieDur) {
  const ts = s.trimStart || 0;
  const te = s.trimEnd && s.trimEnd > ts ? s.trimEnd : s.songDurationSec || 0;
  const base = te > ts ? te - ts : s.songDurationSec || 0;
  if (s.loop && movieDur > 0) return Math.max(0, movieDur - (s.movieOffset || 0));
  return base;
}

// Linear gain (0..volume) for a song at movie-time t, honoring its fade in/out.
// Overlapping two songs (one fading out, next fading in) yields a crossfade.
export function songGainAt(s, t, movieDur) {
  const start = s.movieOffset || 0;
  const len = songLength(s, movieDur);
  if (len <= 0 || t < start || t >= start + len) return 0;
  const vol = s.volume == null ? 1 : s.volume;
  const fi = s.fadeInSec || 0;
  const fo = s.fadeOutSec || 0;
  let g = vol;
  const into = t - start;
  const toEnd = start + len - t;
  if (fi > 0 && into < fi) g *= into / fi;
  if (fo > 0 && toEnd < fo) g *= toEnd / fo;
  return Math.max(0, Math.min(g, 1));
}

// Where inside the source track a song should be at movie-time t.
// Looping songs wrap back to trimStart every slice length (mirrors the
// render's aloop of the trimmed slice).
export function songPosAt(s, t) {
  const start = s.movieOffset || 0;
  const ts = s.trimStart || 0;
  const elapsed = Math.max(0, t - start);
  if (s.loop) {
    const win = songLength(s);
    if (win > 0) return ts + (elapsed % win);
  }
  return ts + elapsed;
}

// ---- per-clip fade to black/white -----------------------------------------
// A clip can fade in at its start and/or fade out at its end, each with its own
// duration + color, stored as `clip.fadeIn` / `clip.fadeOut` = { sec, color }
// where color is 'black' (default) or 'white'. Durations are in the clip's
// TIMELINE (post-retime) seconds, mirroring the render's `fade` filter which
// runs after the speed retime. Returns null when the fade is absent/too short.
export const CLIP_FADE_MAX_SEC = 5;
export function clipFade(clip, key) {
  const f = clip ? clip[key] : null;
  const sec = Number(f && f.sec);
  if (!Number.isFinite(sec) || sec < 0.05) return null;
  return { sec: Math.min(CLIP_FADE_MAX_SEC, sec), color: f.color === 'white' ? 'white' : 'black' };
}
export const clipFadeIn = (c) => clipFade(c, 'fadeIn');
export const clipFadeOut = (c) => clipFade(c, 'fadeOut');

// Master visual/audio multiplier for movie-level intro/outro fade-to-black.
// Returns { audio: 0..1, black: 0..1 } where black is overlay opacity.
export function fadeToBlack(t, totalDur, intro = 0, outro = 0) {
  let f = 1;
  if (intro > 0 && t < intro) f = Math.min(f, t / intro);
  if (outro > 0 && t > totalDur - outro) f = Math.min(f, Math.max(0, (totalDur - t) / outro));
  f = Math.max(0, Math.min(1, f));
  return { audio: f, black: 1 - f };
}
